import { useRef, useState, type ReactNode } from "react";
import {
  CheckCircle2,
  ClipboardCopy,
  ExternalLink,
  MousePointerClick,
  Search,
  Send,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import type { PreferredHost } from "@spectree/shared";
import { PREREQUISITES } from "@/lib/chat/cli-guidance";
import { cn } from "@/lib/utils";
import { useResolvedHost } from "@/hooks/use-resolved-host";
import { useScrollSpotlight } from "../scroll-spotlight";

const MYTECH_URL = `https://${PREREQUISITES.myTechUrl}`;

// The MyTech access request differs by host: a Copilot-host user needs BOTH a
// GitHub Copilot license AND GitHub Enterprise org access (one form covers both);
// a Claude-host user signs in to Claude Code with their own Claude account in the
// browser (self-service, no request) and only needs GitHub Enterprise org access
// for SpecTree's private repos + skill packs.
const SUGGESTED_DESCRIPTION: Record<PreferredHost, string> = {
  copilot: `Hi,

I need access to use SpecTree. To get started, I need:

1. GitHub Copilot CLI Tools — a GitHub Copilot license so I can use the Copilot CLI tools SpecTree depends on.

2. GitHub Enterprise Access — access to the TTC-SpecTree organization so I can open the SpecTree repositories and packages.

Thank you!`,
  claude: `Hi,

I need access to use SpecTree. To get started, I need:

1. GitHub Enterprise Access — access to the TTC-SpecTree organization so I can open the SpecTree repositories and packages.

(I'll sign in to Claude Code with my own Claude account in the browser — no license request needed.)

Thank you!`,
};

// The MyTech search term that surfaces the right service-request template.
const MYTECH_SEARCH_TERM: Record<PreferredHost, string> = {
  copilot: "GitHub Copilot",
  claude: "GitHub Enterprise",
};

/* -------------------------------------------------------------------------- */
/*  Reusable step block                                                       */
/* -------------------------------------------------------------------------- */

function StepBlock({
  number,
  icon,
  title,
  description,
  children,
  isLast,
}: {
  number: number;
  icon: ReactNode;
  title: string;
  description: string;
  children?: ReactNode;
  isLast?: boolean;
}) {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.1] bg-white/[0.06] text-sm font-bold text-foreground">
          {number}
        </span>
        {!isLast && <div className="mt-2 w-px flex-1 bg-white/[0.06]" />}
      </div>
      <div className="flex-1 pb-6">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground/60">{icon}</span>
          <p className="text-sm font-semibold text-foreground">{title}</p>
        </div>
        <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{description}</p>
        {children && <div className="mt-4">{children}</div>}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Access Permissions step                                                   */
/* -------------------------------------------------------------------------- */

export function AccessPermissionsStep() {
  const host = useResolvedHost();
  const suggestedDescription = SUGGESTED_DESCRIPTION[host];
  const searchTerm = MYTECH_SEARCH_TERM[host];
  const [selection, setSelection] = useState<"yes" | "no" | null>(null);
  const [copied, setCopied] = useState(false);
  const infoRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);
  useScrollSpotlight(infoRef);
  useScrollSpotlight(pickerRef);
  useScrollSpotlight(guideRef);

  const copyDescription = async () => {
    try {
      await navigator.clipboard.writeText(suggestedDescription);
      setCopied(true);
      setTimeout(() => { setCopied(false); }, 2000);
    } catch {
      // Clipboard API may not be available in all contexts
    }
  };

  return (
    <div className="space-y-6">
      {/* Access permissions info */}
      <div ref={infoRef} className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-6">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/[0.08] bg-emerald-500/10 text-emerald-400">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <p className="text-base font-semibold text-foreground">
              You need permission to use two services
            </p>
            <p className="mt-2 text-sm leading-7 text-muted-foreground">
              Think of it like getting into a locked building. You need <strong className="text-foreground/90">two keys</strong> before SpecTree will work:
            </p>
            <ul className="mt-3 space-y-2.5">
              <li className="flex items-start gap-3 text-sm leading-6 text-muted-foreground">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-violet-500/15 text-[11px] font-bold text-violet-400">1</span>
                <span>
                  {host === "copilot" ? (
                    <><strong className="text-foreground/90">GitHub Copilot access</strong> — this lets the Copilot command-line helper run. Without it, SpecTree cannot talk to Copilot.</>
                  ) : (
                    <><strong className="text-foreground/90">A Claude account</strong> — you sign in to Claude Code in your browser the first time you run it. A Claude Pro, Max, Team, or Enterprise (or Console) account works; no license to request.</>
                  )}
                </span>
              </li>
              <li className="flex items-start gap-3 text-sm leading-6 text-muted-foreground">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-500/15 text-[11px] font-bold text-blue-400">2</span>
                <span><strong className="text-foreground/90">GitHub Enterprise access</strong> — this lets you reach SpecTree's private code and downloads. Without it, installs will fail.</span>
              </li>
            </ul>
            <p className="mt-4 text-sm leading-6 text-muted-foreground">
              {host === "copilot" ? (
                <>The good news: <strong className="text-foreground/90">one short form asks for both.</strong> It usually takes about 2 minutes.</>
              ) : (
                <>The good news: <strong className="text-foreground/90">one short MyTech form covers the GitHub Enterprise access</strong>, and your Claude account is just a browser sign-in. It usually takes about 2 minutes.</>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Two-card picker */}
      <div ref={pickerRef} className="space-y-6">
        <p className="text-sm font-medium text-muted-foreground">Do you already have both of these?</p>
        <div className="grid gap-4 sm:grid-cols-2">
          {/* YES card */}
          <button
            type="button"
            onClick={() => { setSelection("yes"); }}
            className={cn(
              "flex items-center gap-4 rounded-xl border p-5 text-left transition-all",
              selection === "yes"
                ? "border-emerald-500/30 bg-emerald-500/[0.06] ring-1 ring-emerald-500/20"
                : "border-white/[0.06] bg-white/[0.03] hover:border-white/[0.12] hover:bg-white/[0.05]"
            )}
          >
            <div className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors",
              selection === "yes"
                ? "bg-emerald-500/15 text-emerald-400"
                : "bg-white/[0.06] text-muted-foreground/50"
            )}>
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div>
              <p className={cn("text-sm font-semibold", selection === "yes" ? "text-emerald-300" : "text-foreground")}>
                Yes, I have both
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {"I've"} already been approved — skip ahead
              </p>
            </div>
            {selection === "yes" && (
              <span className="ml-auto shrink-0 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-0.5 text-[11px] font-medium text-emerald-400">
                Ready
              </span>
            )}
          </button>

          {/* NO card */}
          <button
            type="button"
            onClick={() => { setSelection("no"); }}
            className={cn(
              "flex items-center gap-4 rounded-xl border p-5 text-left transition-all",
              selection === "no"
                ? "border-amber-500/30 bg-amber-500/[0.06] ring-1 ring-amber-500/20"
                : "border-white/[0.06] bg-white/[0.03] hover:border-white/[0.12] hover:bg-white/[0.05]"
            )}
          >
            <div className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors",
              selection === "no"
                ? "bg-amber-500/15 text-amber-400"
                : "bg-white/[0.06] text-muted-foreground/50"
            )}>
              <XCircle className="h-5 w-5" />
            </div>
            <div>
              <p className={cn("text-sm font-semibold", selection === "no" ? "text-amber-300" : "text-foreground")}>
                No, I need to request them
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {"I'll"} show you how — takes 2 minutes
              </p>
            </div>
          </button>
        </div>
      </div>

      {/* Guide — shows when "No" is selected */}
      {selection === "no" && (
        <div ref={guideRef} className="rounded-xl border border-amber-500/20 bg-white/[0.02] px-6 pb-6 pt-5 space-y-8">
          <p className="text-sm font-semibold text-foreground">
            Follow these 5 easy steps to request access
          </p>

          {/* Step 1 — Open MyTech */}
          <StepBlock
            number={1}
            icon={<ExternalLink className="h-4 w-4" />}
            title="Open the MyTech website"
            description="This is the place where you ask for work tools. Click the button — it opens in a new tab. If it asks you to log in, use your normal work email and password."
          >
            <a
              href={MYTECH_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.05] px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-white/[0.1]"
            >
              Open MyTech Portal
              <ExternalLink className="h-4 w-4" />
            </a>
          </StepBlock>

          {/* Step 2 — Search */}
          <StepBlock
            number={2}
            icon={<Search className="h-4 w-4" />}
            title={`Type "${searchTerm}" in the search bar`}
            description="At the very top of the page there's a big search box. Click on it and type these exact words:"
          >
            <div className="flex items-center gap-3 rounded-lg border border-white/[0.08] bg-[#0D0D0F] px-4 py-3">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground/50" />
              <span className="font-mono text-sm text-foreground">{searchTerm}</span>
            </div>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              A little dropdown will pop up underneath. {"You'll"} see something that says:
            </p>
            <div className="mt-2 rounded-lg border border-white/[0.08] bg-white/[0.04] px-4 py-3">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground/60 mb-1">Service Request Template (1)</p>
              <p className="text-sm font-medium text-foreground">Software Tool Administration – GitHub Enterprise</p>
            </div>
          </StepBlock>

          {/* Step 3 — Click */}
          <StepBlock
            number={3}
            icon={<MousePointerClick className="h-4 w-4" />}
            title="Click on that result"
            description={'It might look confusing because the name says "GitHub Enterprise" — but this is the right one! This single form lets you ask for both things at once. Click on it to open the request form.'}
          />

          {/* Step 4 — Paste */}
          <StepBlock
            number={4}
            icon={<ClipboardCopy className="h-4 w-4" />}
            title='Paste this message into the "Detailed Description" box'
            description={"You'll see a form with a big text box labeled Detailed Description. Copy the message below and paste it straight in. That's it — nothing to change!"}
          >
            <div className="rounded-xl border border-white/[0.08] bg-[#0D0D0F] overflow-hidden">
              <div className="flex items-center justify-between border-b border-white/[0.06] px-4 py-2.5">
                <span className="text-xs font-medium text-muted-foreground">Paste this into the text box</span>
                <button
                  type="button"
                  onClick={() => void copyDescription()}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-all",
                    copied
                      ? "bg-emerald-500/10 text-emerald-400"
                      : "bg-white/[0.06] text-muted-foreground hover:bg-white/[0.1] hover:text-foreground"
                  )}
                >
                  <ClipboardCopy className="h-3 w-3" />
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
              <pre className="whitespace-pre-wrap px-4 py-3.5 font-mono text-[13px] leading-6 text-foreground/80">
                {suggestedDescription}
              </pre>
            </div>
          </StepBlock>

          {/* Step 5 — Submit */}
          <StepBlock
            number={5}
            icon={<Send className="h-4 w-4" />}
            title={"Click \"Request Now\" and you're done!"}
            description={"Scroll down past the other fields — you don't need to fill anything else in. Just hit the \"Request Now\" button at the bottom. You'll get an email when your request is approved (usually 1–2 business days). Once approved, come back and select \"Yes, I have both\" above."}
            isLast
          />
        </div>
      )}

      {/* Confirmation when YES is selected */}
      {selection === "yes" && (
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-5 py-4 text-sm text-emerald-100/90">
          <p className="font-semibold">{"You're all set — click Next to continue."}</p>
        </div>
      )}
    </div>
  );
}
